import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Search } from 'lucide-react';
import { useData } from '../../context/DataContext'; // Import useData
import {
  Service,
  Department,
  NewsArticle,
  Employee,
  Officer,
  PublicInfoItem,
  EmergencyContact,
  MobileApplication,
} from '../../types/domain';

interface GlobalSearchProps {
  onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ onClose }) => {
  const {
    services, departments, news, officers,
    publicInfoItems,
    emergencyContacts,
    mobileApps
  } = useData(); // Consume data

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{
    services: Service[];
    departments: Department[];
    news: NewsArticle[];
    staff: Employee[];
    officers: Officer[];
    publicInfoItems: PublicInfoItem[];
    emergencyContacts: EmergencyContact[];
    mobileApplications: MobileApplication[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query.trim()) {
        setSearchResults(null);
        return;
      }
      setIsLoading(true);
      try {
        const q = query.toLowerCase();

        // Perform local filtering with null safety
        const filteredServices = (services || []).filter((s: any) =>
          s.title?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q)
        );

        const filteredDepartments = (departments || []).filter((d: any) =>
          d.name?.toLowerCase().includes(q) || (d.description && d.description.toLowerCase().includes(q))
        );

        const filteredNews = (news || []).filter((n: any) =>
          n.title?.toLowerCase().includes(q) || (n.summary && n.summary.toLowerCase().includes(q))
        );

        // Assuming no separate 'staff' collection in context yet, skipping or using officers
        const filteredStaff: Employee[] = [];

        const filteredOfficers = (officers || []).filter((o: any) =>
          o.name?.toLowerCase().includes(q) || (o.post && o.post.toLowerCase().includes(q))
        );

        const filteredPublicInfo = (publicInfoItems || []).filter((i: any) =>
          i.title?.toLowerCase().includes(q) || (i.description && i.description.toLowerCase().includes(q))
        );

        const filteredContacts = (emergencyContacts || []).filter((c: any) =>
          c.service?.toLowerCase().includes(q) || c.number?.includes(q)
        );

        const filteredApps = (mobileApps || []).filter((a: any) =>
          a.name?.toLowerCase().includes(q) || (a.description && a.description.toLowerCase().includes(q))
        );

        setSearchResults({
          services: filteredServices,
          departments: filteredDepartments,
          news: filteredNews,
          staff: filteredStaff,
          officers: filteredOfficers,
          publicInfoItems: filteredPublicInfo,
          emergencyContacts: filteredContacts,
          mobileApplications: filteredApps
        });

      } catch (error) {
        console.error('Error during global search:', error);
        setSearchResults({
          services: [],
          departments: [],
          news: [],
          staff: [],
          officers: [],
          publicInfoItems: [],
          emergencyContacts: [],
          mobileApplications: [],
        });
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimeout = setTimeout(() => {
      fetchResults();
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [query]);


  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);


  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl shadow-2xl bg-surface border border-border">

        <div className="flex items-center gap-3 p-6 border-b border-border">
          <Search className="w-6 h-6 text-muted-foreground" />
          <input
            autoFocus
            type="text"
            placeholder="Search services, departments, news, staff, officers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-lg outline-none text-foreground placeholder-muted-text"
          />
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>


        <div className="max-h-96 overflow-y-auto bg-surface">
          {isLoading && (
            <div className="p-6 text-center text-muted-foreground">
              Searching...
            </div>
          )}
          {!isLoading && query && searchResults && Object.values(searchResults).every(arr => arr.length === 0) ? (
            <div className="p-6 text-center text-muted-foreground">
              No results found for "{query}"
            </div>
          ) : (
            searchResults && (
              <>

                {searchResults.services.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">SERVICES</div>
                    {searchResults.services.map((service, idx) => (
                      <Link key={idx} to={`/services?search=${encodeURIComponent(service.title)}`} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{service.title}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{service.description?.substring(0, 60)}...</p>
                      </Link>
                    ))}
                  </div>
                )}


                {searchResults.departments.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">DEPARTMENTS</div>
                    {searchResults.departments.map((dept, idx) => (
                      <Link key={idx} to={`/departments?id=${dept.id}`} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{dept.name}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{dept.description?.substring(0, 60)}...</p>
                      </Link>
                    ))}
                  </div>
                )}


                {searchResults.news.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">NEWS</div>
                    {searchResults.news.map((article, idx) => (
                      <Link key={idx} to={`/news?id=${article.id}`} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{article.title}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{article.summary?.substring(0, 60)}...</p>
                      </Link>
                    ))}
                  </div>
                )}


                {searchResults.staff.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">STAFF</div>
                    {searchResults.staff.map((emp, idx) => (
                      <Link key={idx} to={`/staff?search=${emp.name}`} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{emp.name}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{emp.post}</p>
                      </Link>
                    ))}
                  </div>
                )}


                {searchResults.officers.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">OFFICERS</div>
                    {searchResults.officers.map((officer, idx) => (
                      <Link key={idx} to={`/staff?search=${officer.name}`} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{officer.name}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{officer.post}</p>
                      </Link>
                    ))}
                  </div>
                )}


                {searchResults.publicInfoItems.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">PUBLIC INFO</div>
                    {searchResults.publicInfoItems.map((item, idx) => (
                      <Link key={idx} to={item.link} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{item.title}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{item.description?.substring(0, 60)}...</p>
                      </Link>
                    ))}
                  </div>
                )}


                {searchResults.emergencyContacts.length > 0 && (
                  <div className="border-b border-border">
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">EMERGENCY CONTACTS</div>
                    {searchResults.emergencyContacts.map((contact, idx) => (
                      <a key={idx} href={contact.tel_link} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{contact.service}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{contact.number}</p>
                      </a>
                    ))}
                  </div>
                )}


                {searchResults.mobileApplications.length > 0 && (
                  <div>
                    <div className="px-6 py-2 text-xs font-bold text-muted-foreground">MOBILE APPLICATIONS</div>
                    {searchResults.mobileApplications.map((app, idx) => (
                      <a key={idx} href={app.url || '#'} onClick={onClose} className="block px-6 py-3 hover:bg-muted/10 cursor-pointer transition-colors text-foreground">
                        <p className="font-semibold">{app.name}</p>
                        <p className="text-sm opacity-75 text-muted-foreground">{app.description?.substring(0, 60)}...</p>
                      </a>
                    ))}
                  </div>
                )}
              </>
            )
          )}
        </div>


        <div className="p-3 text-xs text-center text-muted-foreground border-t border-border">
          Press <kbd className="px-2 py-1 rounded bg-muted text-xs font-bold text-foreground">ESC</kbd> to close
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
